// ============================================
// Open Issues 问题管理系统类型定义
// ============================================

// 问题类型
export type IssueType = 'dimension' | 'appearance' | 'function' | 'other';

// 问题阶段状态 (0/4 ~ 4/4)
export type IssueStatus = '0/4' | '1/4' | '2/4' | '3/4' | '4/4';

// 问题风险状态
export type RiskStatus = 'closed' | 'normal' | 'delayed' | 'breakpoint-failed';

// 问题清单类型
export type IssueListType = 'product' | 'production';

// 工作计划任务状态
export type PlanTaskStatus = 'pending' | 'in-progress' | 'completed' | 'delayed' | 'delayed-completed';

// 整改措施状态
export type ActionItemStatus = 'pending' | 'completed' | 'bp-ng' | 'delayed-complete';

// ============================================
// 问题主模型
// ============================================
export interface Issue {
  id: string;
  projectId: string;
  issueType: IssueListType;
  sequence: number;
  
  // 基础信息
  description: string;
  type: IssueType;
  attachments: Attachment[];
  openDate: string;
  vehicleNode?: string;
  
  // 整改信息
  planTasks: PlanTask[];
  rootCause?: string;
  
  // 短期措施
  shortTermAction?: string;
  stBreakpoint?: string;
  stBreakpointNode?: string;
  
  // 长期措施
  longTermAction?: string;
  ltBreakpoint?: string;
  ltBreakpointNode?: string;
  
  // 状态信息
  status: IssueStatus;
  riskStatus: RiskStatus;
  owner?: string;
  isDelayed: boolean;
  hasBreakpointFailed: boolean;
  
  // 时间戳
  createdAt: string;
  updatedAt: string;
  closedAt?: string;

  // 产线问题特有字段
  station?: string;

  // 验证记录
  verificationHistory: VerificationRecord[];

  // 整改措施（多措施支持）
  actionItems: ActionItem[];
}

// ============================================
// 整改措施模型
// ============================================
export interface ActionItem {
  id: string;
  issueId: string;
  actionType: 'short_term' | 'long_term';
  sequence: number;
  content: string;
  plannedDate?: string;
  breakpointDate?: string;
  breakpointNode?: string;
  status: ActionItemStatus;
  delayReason?: string;
  newPlannedDate?: string;
  newBpNode?: string;
  actualDate?: string;
  newBpReason?: string;
  newBpDate?: string;
  newBpNode2?: string;
  delayHistory: ActionItemDelayRecord[];
  createdAt: string;
  updatedAt: string;
}

// 措施延期记录
export interface ActionItemDelayRecord {
  id: string;
  actionItemId: string;
  originalPlannedDate: string;
  newPlannedDate: string;
  bpNode?: string;
  delayReason: string;
  isBpNg: boolean;
  recordedAt: string;
  recordedBy: string;
}

// DTO
export interface CreateActionItemDTO {
  issueId: string;
  actionType: 'short_term' | 'long_term';
  content: string;
  breakpointDate?: string;
  breakpointNode?: string;
}

export interface UpdateActionItemDTO {
  id: string;
  content?: string;
  plannedDate?: string;
  breakpointDate?: string;
  breakpointNode?: string;
  status?: ActionItemStatus;
  delayReason?: string;
  newPlannedDate?: string;
  newBpNode?: string;
  actualDate?: string;
  newBpReason?: string;
  newBpDate?: string;
  newBpNode2?: string;
}

// ============================================
// 工作计划任务模型
// ============================================
export interface PlanTask {
  id: string;
  issueId: string;
  sequence: number;
  content: string;
  plannedDate: string;
  actualDate?: string;
  status: PlanTaskStatus;
  delayReason?: string;
  newPlannedDate?: string;
  delayHistory: TaskDelayRecord[];
  createdAt: string;
  updatedAt: string;
}

// 任务延期记录
export interface TaskDelayRecord {
  id: string;
  taskId: string;
  originalPlannedDate: string;
  newPlannedDate: string;
  delayReason: string;
  recordedAt: string;
  recordedBy: string;
}

// ============================================
// 附件模型（Vercel Blob存储）
// ============================================
export interface Attachment {
  id: string;
  issueId: string;
  name: string;
  url: string;
  type: string;
  size: number;
  uploadedAt: string;
  uploadedBy: string;
}

// ============================================
// 断点验证记录
// ============================================
export interface VerificationRecord {
  id: string;
  issueId: string;
  verifiedAt: string;
  verifiedBy: string;
  result: 'pass' | 'fail';
  breakpointType: 'S/T' | 'L/T';
  failureReason?: string;
  newPlan?: string;
  newBreakpoint?: string;
}

// ============================================
// Dashboard 数据模型
// ============================================
export interface DashboardData {
  totalIssues: number;
  statusDistribution: StatusDistribution;
  riskWarnings: RiskWarnings;
  typeDistribution: TypeDistribution;
  riskStatusDistribution: RiskStatusDistribution;
  avgResolutionTime: number;
  weeklyStats: WeeklyStats;
}

export interface StatusDistribution {
  '0/4': number;
  '1/4': number;
  '2/4': number;
  '3/4': number;
  '4/4': number;
}

export interface RiskWarnings {
  delayed: number;
  breakpointFailed: number;
}

export interface TypeDistribution {
  dimension: number;
  appearance: number;
  function: number;
  other: number;
}

export interface RiskStatusDistribution {
  closed: number;
  normal: number;
  delayed: number;
  'breakpoint-failed': number;
}

export interface WeeklyStats {
  newIssues: number;
  closedIssues: number;
  updatedIssues: number;
  expiringBreakpoints: number;
}

// ============================================
// 今日任务数据模型
// ============================================
export interface TodayTasks {
  riskWarnings: RiskWarnings;
  expiringPlanTasks: ExpiringTask[];
  expiringStBreakpoints: ExpiringBreakpoint[];
  expiringLtBreakpoints: ExpiringBreakpoint[];
}

export interface ExpiringTask {
  issueId: string;
  issueSequence: number;
  taskId: string;
  taskContent: string;
  plannedDate: string;
  owner: string;
}

export interface ExpiringBreakpoint {
  issueId: string;
  issueSequence: number;
  issueDescription: string;
  breakpointType: 'S/T' | 'L/T';
  breakpointDate: string;
  owner: string;
}

// ============================================
// 筛选条件模型
// ============================================
export interface IssueFilters {
  status: IssueStatus[];
  type: IssueType[];
  riskStatus: RiskStatus[];
  owner: string[];
  dateRange: {
    start: string;
    end: string;
  };
  breakpointStatus: 'all' | 'expired' | 'expiring' | 'normal';
  keyword: string;
}

// ============================================
// API 请求/响应 DTO
// ============================================
export interface CreateIssueDTO {
  projectId: string;
  issueType: IssueListType;
  description: string;
  type: IssueType;
  status?: string;
  openDate: string;
  vehicleNode?: string;
  attachments?: Omit<Attachment, 'id' | 'issueId'>[];
  planTasks?: Omit<PlanTask, 'id' | 'issueId' | 'createdAt' | 'updatedAt' | 'delayHistory'>[];
  rootCause?: string;
  shortTermAction?: string;
  stBreakpoint?: string;
  stBreakpointNode?: string;
  longTermAction?: string;
  ltBreakpoint?: string;
  ltBreakpointNode?: string;
  owner?: string;
  station?: string;
  actionItems?: CreateActionItemDTO[];
}

export interface UpdateIssueDTO {
  description?: string;
  type?: IssueType;
  openDate?: string;
  vehicleNode?: string;
  rootCause?: string;
  shortTermAction?: string;
  stBreakpoint?: string;
  stBreakpointNode?: string;
  longTermAction?: string;
  ltBreakpoint?: string;
  ltBreakpointNode?: string;
  status?: IssueStatus;
  owner?: string;
  station?: string;
  attachments?: Omit<Attachment, 'id' | 'issueId'>[];
}

export interface CreatePlanTaskDTO {
  content: string;
  plannedDate: string;
}

export interface UpdatePlanTaskDTO {
  content?: string;
  plannedDate?: string;
  status?: PlanTaskStatus;
  actualDate?: string | null;
  newPlannedDate?: string | null;
  delayReason?: string | null;
}

export interface CompleteTaskDTO {
  actualDate: string;
  completionNote?: string;
}

export interface DelayTaskDTO {
  delayReason: string;
  newPlannedDate: string;
  recordedBy: string;
}

export interface VerifyBreakpointDTO {
  result: 'pass' | 'fail';
  breakpointType: 'S/T' | 'L/T';
  actualDate?: string;
  verificationNote?: string;
  failureReason?: string;
  newPlan?: string;
  newBreakpoint?: string;
}

// ============================================
// 文档导入相关
// ============================================
export interface ImportPreview {
  issues: ImportIssueItem[];
  totalCount: number;
}

export interface ImportIssueItem {
  description: string;
  type: IssueType;
  confidence: number;
  status?: string;
  openDate?: string;
  plan?: string;
  rootCause?: string;
  shortTermAction?: string;
  stBreakpoint?: string;
  longTermAction?: string;
  ltBreakpoint?: string;
  owner?: string;
  station?: string;
}

// ============================================
// 颜色常量
// ============================================
export const ISSUE_STATUS_COLORS: Record<IssueStatus, string> = {
  '0/4': '#f44245',
  '1/4': '#f59e0b',
  '2/4': '#eab308',
  '3/4': '#2a36e2',
  '4/4': '#57e434',
};

export const RISK_STATUS_COLORS: Record<RiskStatus, string> = {
  'closed': '#57e434',
  'normal': '#2a36e2',
  'delayed': '#f59e0b',
  'breakpoint-failed': '#f44245',
};

export const ISSUE_TYPE_COLORS: Record<IssueType, { bg: string; text: string }> = {
  dimension: { bg: '#ede7fb', text: '#222222' },
  appearance: { bg: '#fbe7e9', text: '#222222' },
  function: { bg: '#fff0bb', text: '#222222' },
  other: { bg: '#d4f8d3', text: '#222222' },
};

export const ISSUE_TYPE_LABELS: Record<IssueType, string> = {
  dimension: '尺寸',
  appearance: '外观',
  function: '功能',
  other: '其他',
};

export const ISSUE_STATUS_LABELS: Record<IssueStatus, string> = {
  '0/4': '问题开启',
  '1/4': '原因分析',
  '2/4': '短期措施',
  '3/4': '长期措施',
  '4/4': '问题关闭',
};

export const RISK_STATUS_LABELS: Record<RiskStatus, string> = {
  'closed': '问题关闭',
  'normal': '正常进行',
  'delayed': '任务延期',
  'breakpoint-failed': '断点失效',
};

export const PLAN_TASK_STATUS_COLORS: Record<PlanTaskStatus, string> = {
  'pending': '#9ca3af',
  'in-progress': '#2a36e2',
  'completed': '#57e434',
  'delayed': '#f44245',
  'delayed-completed': '#f44245',
};

export const PLAN_TASK_STATUS_LABELS: Record<PlanTaskStatus, string> = {
  'pending': '待执行',
  'in-progress': '进行中',
  'completed': '已完成',
  'delayed': '已延期',
  'delayed-completed': '延期完成',
};
